import { Observation, Patient } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Select, Textarea, TextInput } from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { parseDateInputValue } from '@/utils/dateInput.ts'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { formatDateTime } from '@/utils.ts'
import { useState } from 'react'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'category', title: 'Category' },
  { code: 'description', title: 'Description' },
  { code: 'date', title: 'Date' },
  { code: 'comment', title: 'Comment' },
  { code: 'doctor', title: 'Doctor' },
]

// Common social-history LOINC codes (subset, covers the everyday cases).
const SOCIAL_HISTORY_TYPES = [
  { value: '72166-2', label: 'Tobacco smoking status', system: 'http://loinc.org' },
  { value: '74013-4', label: 'Alcohol use', system: 'http://loinc.org' },
  { value: '11343-1', label: 'Drug use - history', system: 'http://loinc.org' },
  { value: '11341-5', label: 'Sexual history', system: 'http://loinc.org' },
  { value: '67875-5', label: 'Occupation', system: 'http://loinc.org' },
  { value: '74006-8', label: 'Educational attainment', system: 'http://loinc.org' },
  { value: '11295-3', label: 'Exercise / physical activity', system: 'http://loinc.org' },
  { value: 'other', label: 'Other social history', system: 'http://loinc.org' },
]

const renderColumn = (resource: Observation, itemCode: string): string => {
  switch (itemCode) {
    case 'category':
      return resource.code?.coding?.[0]?.display || resource.code?.text || '—'
    case 'description':
      if (resource.valueString) return resource.valueString
      if (resource.valueCodeableConcept)
        return (
          resource.valueCodeableConcept.text ||
          resource.valueCodeableConcept.coding?.[0]?.display ||
          resource.valueCodeableConcept.coding?.[0]?.code ||
          ''
        )
      if (resource.valueQuantity) return resource.valueQuantity.value?.toString() || ''
      if (resource.valueBoolean !== undefined) return resource.valueBoolean ? 'Yes' : 'No'
      return ''
    case 'date': {
      const date =
        resource.effectiveDateTime ||
        (resource.effectivePeriod &&
          resource.effectivePeriod.start + ' — ' + resource.effectivePeriod.end) ||
        resource.issued ||
        ''
      return date ? formatDateTime(date, '-', true) : '-'
    }
    case 'comment':
      return resource.note?.map((n) => n.text).join('; ') || ''
    case 'doctor':
      if (resource.performer && resource.performer.length > 0)
        return resource.performer.map((p) => p.display).join(', ')
      return ''
    default:
      return ''
  }
}

const preprocessList = (list: Observation[]): Observation[] => {
  const resourceList: Observation[] = []
  for (const item of list) {
    if (
      !resourceList.some(
        (r) =>
          (r.effectiveDateTime && r.effectiveDateTime === item.effectiveDateTime) ||
          (r.issued && r.issued === item.issued),
      )
    ) {
      resourceList.push(item)
    }
  }

  return resourceList
}

export const SocialHistory = ({ patient, item }: Props) => {
  const [typeCode, setTypeCode] = useState<string | null>(null)
  const [customLabel, setCustomLabel] = useState('')
  const [value, setValue] = useState('')
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(new Date())
  const [note, setNote] = useState('')

  const resetForm = () => {
    setTypeCode(null)
    setCustomLabel('')
    setValue('')
    setEffectiveDate(new Date())
    setNote('')
  }

  const buildResource = (): Observation | { error: string } => {
    if (!typeCode || !value.trim()) {
      return { error: 'Please select a category and enter a value.' }
    }
    if (typeCode === 'other' && !customLabel.trim()) {
      return { error: 'Please describe the social history category.' }
    }

    const typeOption = SOCIAL_HISTORY_TYPES.find((t) => t.value === typeCode)
    const isOther = typeCode === 'other'

    return {
      resourceType: 'Observation',
      status: 'final',
      subject: { reference: `Patient/${patient.id}` },
      category: [
        {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/observation-category',
              code: 'social-history',
              display: 'Social History',
            },
          ],
          text: 'social-history',
        },
      ],
      code: {
        coding: isOther
          ? [{ system: typeOption?.system, display: customLabel.trim() }]
          : [
              {
                system: typeOption?.system,
                code: typeCode,
                display: typeOption?.label,
              },
            ],
        text: isOther ? customLabel.trim() : typeOption?.label,
      },
      effectiveDateTime: effectiveDate ? effectiveDate.toISOString() : new Date().toISOString(),
      issued: new Date().toISOString(),
      valueString: value.trim(),
      note: note.trim() ? [{ text: note.trim() }] : undefined,
    }
  }

  return (
    <AddResourceSection<Observation>
      patient={patient}
      title="Social History"
      modalTitle="Add Social History"
      resourceType="Observation"
      featureContext="Observation (social-history)"
      noun="social history"
      isSubmitDisabled={!typeCode || !value.trim()}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <Select
            label="Category *"
            data={SOCIAL_HISTORY_TYPES.map((t) => ({ value: t.value, label: t.label }))}
            value={typeCode}
            onChange={setTypeCode}
            searchable
            placeholder="Select category"
          />
          {typeCode === 'other' && (
            <TextInput
              label="Describe category *"
              placeholder="e.g. Living arrangement"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.currentTarget.value)}
            />
          )}
          <TextInput
            label="Value *"
            placeholder="e.g. Never smoker, 2 drinks/week"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
          />
          <DateInput
            label="Date"
            value={effectiveDate}
            onChange={(v) => setEffectiveDate(parseDateInputValue(v))}
            clearable
            placeholder="Pick a date"
          />
          <Textarea
            label="Notes"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder="Optional notes"
            minRows={2}
          />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="date"
          blockRemove
          preprocessList={preprocessList}
        />
      )}
    </AddResourceSection>
  )
}
